import * as React from "react";
import { Formik } from "formik";
import * as yup from "yup";
import styled, { css } from "styled-components";
import * as _ from "lodash";

import Button from "../Button/Button";
import { Text } from "../Text";
import {
  IFormProps,
  IFormField,
  Label
} from ".";
import Field from "./Field";
import Fieldset from "./Fieldset";

const FormStyled = styled("form")<{ submitButtonAlignment: "left" | "center" | "right" }>`
  ${({ theme, submitButtonAlignment }) => css`
    label:not(.checkbox-label,.radio-label),
    legend {
      display: block;
      padding-bottom: 4px;
    }

    .form-error {
      box-sizing: border-box;
      margin-bottom: ${theme.spacing.margins.sm};
      padding: ${theme.spacing.padding.sm};
      font-style: italic;
      background: ${theme.colors.errorLight};
      opacity: 0.7;
      border-radius: ${theme.borderRadius};
      border: ${theme.colors.error} 1px solid;
    }

    .form-submit-row {
      display: flex;
      flex-direction: row;
      justify-content: ${submitButtonAlignment};
      padding-top: ${theme.spacing.padding.lg};
    }

    fieldset {
      label {
        position: absolute;
        left: -99999px;
        text-indent: -99999px;
      }

      .form-row {
        padding-top: ${theme.spacing.padding.xs};

        &:first-of-type {
          padding-top: 0;
        }
      }
    }
  `}
`;

const Form: React.ForwardRefRenderFunction<Formik<{ [key: string]: any; }>, IFormProps> = (props, ref) => {
  const buttonAlignmentDefault = "left";
  const buttonAlignment = props.submitButtonAlignment || buttonAlignmentDefault;

  // get the initial value for the form
  const initialValues = (fields: IFormField[]): { [key: string]: any } => {
    return fields.reduce<{ [key: string]: any }>((map, field) => {
      if (field.fields) {
        const nestedValues = initialValues(field.fields);

        return {
          ...map,
          ...nestedValues
        };
      } else {
        // Don't store values for title fields
        if (field.type === "title") {
          return map;
        }

        let value = field.value || "";

        if (field.options && field.type === "checkbox") {
          const values: string[] = [];

          field.options.forEach(opt => {
            if (opt.checked) {
              values.push(opt.text);
            }
          });
          value = values;
        }

        if (field.name) {
          map[ field.name ] = value;
        }

        return map;
      }
    }, {});
  };

  const validationRules = (fields: IFormField[]): { [key: string]: any } => {
    return fields.reduce<any>((map, field) => {
      if (field.fields) {
        const nestedRules = validationRules(field.fields);

        return {
          ...map,
          ...nestedRules
        };
      } else {
        map[ field.name ] = field.validation || null;

        return map;
      }
    }, {});
  };

  return (
    <Formik
      ref={ref}
      onSubmit={async (values: any, actions) => props.onSubmit(values, actions)}
      initialValues={initialValues(props.fields)}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={yup.object().shape(validationRules(props.fields))}
    >
      {({
        handleSubmit, errors, handleChange, handleBlur, setFieldValue, values
      }) => {
        return (
          <FormStyled
            onSubmit={handleSubmit}
            submitButtonAlignment={buttonAlignment}
          >
            {props.error && (
              <div className="form-error">
                <Text weight="bold">
                  {props.error}
                </Text>
              </div>
            )}

            {props.fields.map(field => {
              //@ts-ignore
              field.error = errors[ field.name ];

              // if type is a title, return as is with name value
              if (field.type === "title") {
                return (
                  <FormRow key={field.name}>
                    <Text fontSize="h2">
                      {field.name}
                    </Text>
                  </FormRow>
                );
              }

              // If a fieldset has been supplied
              if (field.type === "fieldset" && field.fields) {
                const containsRequiredfield = !!_.find(field.fields, field => field?.validation?._exclusive?.required);

                return (
                  <FormRow key={field.name}>
                    <Fieldset
                      legend={field.label}
                      required={containsRequiredfield}
                    >
                      {field.fields.map(fieldProps => {
                        //@ts-ignore
                        fieldProps.error = errors[ fieldProps.name ];

                        return (
                          <div key={fieldProps.name}>
                            <div className="form-row">
                              <Field
                                key={fieldProps.name}
                                autoHideLabels={false}
                                field={fieldProps}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                setFieldValue={setFieldValue}
                                value={fieldProps.name ? values[ fieldProps.name ] : ""}
                              />
                            </div>

                            {/* Render custom input field if Custom selected */}
                            {fieldProps.options && values[ fieldProps.name ] === "Custom" && (
                              <div
                                key={`${fieldProps.name}:CUSTOM`}
                                className="form-row"
                              >
                                <Field
                                  key={fieldProps.name}
                                  forceHideLabel
                                  field={{
                                    inputType: "text",
                                    label: `Custom ${fieldProps.label}`,
                                    name: `custom:${fieldProps.name}`,
                                    placeholder: fieldProps.placeholder ? `Custom ${fieldProps.placeholder}` : undefined,
                                    type: "input",
                                    // If custom selected, require input
                                    validation: yup.string().required()
                                  }}
                                  handleChange={handleChange}
                                  handleBlur={handleBlur}
                                  setFieldValue={setFieldValue}
                                  value={values[ `custom:${fieldProps.name}` ] || ""}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </Fieldset>
                  </FormRow>
                );
              }

              // If a component has been supplied, return that component
              if (field.Component) {
                return (
                  <FormRow key={field.name}>
                    <Label
                      for={field.name}
                      label={field.label}
                      required={field.validation?._exclusive?.required}
                    />

                    <field.Component
                      error={field.error}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      value={field.name ? values[ field.name ] : ""}
                      field={field}
                    />
                  </FormRow>
                );
              }

              return (
                <>
                  <FormRow key={field.name}>
                    <Field
                      autoHideLabels={props.autoHideLabels}
                      field={field}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      value={field.name ? values[ field.name ] : ""}
                    />
                  </FormRow>

                  {/* Render custom input field if Custom selected */}
                  {field.options && values[ field.name ] === "Custom" && (
                    <FormRow
                      key={`${field.name}:CUSTOM`}
                      className="form-row"
                    >
                      <Field
                        forceHideLabel
                        field={{
                          inputType: "text",
                          label: `Custom ${field.label}`,
                          name: `custom:${field.name}`,
                          placeholder: field.placeholder ? `Custom ${field.placeholder}` : undefined,
                          type: "input",
                          // If custom selected, require input
                          validation: yup.string().required()
                        }}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        value={values[ `custom:${field.name}` ] || ""}
                      />
                    </FormRow>
                  )}
                </>
              );
            })}

            {props.children}

            {props.submitButtonText && (
              <div className="form-submit-row">
                <Button
                  loading={props.submitting}
                  type="submit"
                  size="medium"
                >
                  {props.submitButtonText}
                </Button>
              </div>
            )}
          </FormStyled>
        );
      }}
    </Formik>
  );
};

export const FormRow = styled.div.attrs({ className: "form-row" })`
  ${({ theme }) => css`
    padding-top: ${theme.spacing.padding.lg};
    width: 100%;

    &:first-of-type {
      padding-top: ${theme.spacing.padding.sm};
    }

    &:first-child {
      padding-top: 0;
    }
  `};
`;

export default React.forwardRef(Form);
